import React from 'react';
import { Checkbox, TextField } from '@mui/material';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { compact, isArray, isObject } from 'lodash';

interface Props<T>
  extends Omit<
    AutocompleteProps<{ label: string; value: T }, boolean, boolean, boolean>,
    'renderInput' | 'value' | 'onChange'
  > {
  backgroundColor?: string;
  name?: string;
  label?: string;
  required?: boolean;
  onReset?: () => void;
  value: Array<T>;
  onChange: (value: Array<T>) => void;
  error?: string;
}

const MultipleAutocomplete = <T,>({
  name,
  id,
  fullWidth = true,
  size = 'small',
  placeholder = '',
  label = '',
  required = false,
  onReset,
  options,
  value,
  onChange,
  error,
  ...rest
}: Props<T>): JSX.Element => {
  const selectedOptions = React.useMemo(
    () => options.filter((v) => value.includes(v.value)),
    [options],
  );

  return (
    <Autocomplete
      fullWidth={fullWidth}
      options={options ?? []}
      getOptionKey={(option) =>
        typeof option === 'string' ? option : String(option.value)
      }
      multiple
      disableCloseOnSelect
      onInputChange={(_e, _value, reason) => {
        if (reason === 'clear') {
          onReset?.();
        }
      }}
      onChange={(_, newValue) => {
        const values = compact(
          isArray(newValue)
            ? newValue.map((v) => (isObject(v) ? v.value : null))
            : newValue
            ? [isObject(newValue) ? newValue.value : null]
            : [],
        );
        onChange(values);
      }}
      value={selectedOptions}
      renderOption={({ key, ...props }, option, { selected }) => (
        <li key={key} {...props}>
          <Checkbox checked={selected} />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={placeholder}
          size={size}
          fullWidth={fullWidth}
          required={required}
          error={!!error}
          helperText={error}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              autoComplete: 'off',
            },
          }}
        />
      )}
      {...rest}
    />
  );
};
export default MultipleAutocomplete;
